import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { directiveFreeText, DirectiveService, VerifiedVaDirective } from '@app/modules/directive/service/directive.service';
import { isMinimalVerifyResponse, MinimalVerifyResponse } from '@app/shared/interfaces/tiered-verify';

/**
 * PUBLIC break-glass page for advance directives (any type) — the directive
 * counterpart of the MOLST lookup: scanning the printed QR lands here, no
 * login, blockchain-verified, every access audited.
 */
@Component({
  selector: 'app-public-directive-verify',
  templateUrl: './public-directive-verify.component.html',
  styleUrls: ['../../../modules/consent/consent-shared.css', './public-directive-verify.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicDirectiveVerifyComponent implements OnInit {
  public loading = true;
  public notFound = false;
  public directive: VerifiedVaDirective | null = null;
  // MINIMAL tier (unregistered device): emergency summary instead of the directive.
  public minimal: MinimalVerifyResponse | null = null;

  /** The document's own type — this page must never claim a Maryland directive is a VA form. */
  public get docTypeLabel(): string {
    switch (this.directive?.docType) {
      case 'md_advance_directive':
        return 'Maryland Advance Directive';
      case 'va_10_0137':
        return 'VA Advance Directive (Form 10-0137)';
      default:
        return 'Advance directive';
    }
  }

  /**
   * The patient's written words (living-will free text, questionnaire content
   * left by "Apply my answers"). Rendered as their own block with line breaks
   * preserved — inlined into a bullet list they collapse into a wall of text.
   */
  public get freeText(): string[] {
    return this.directive ? directiveFreeText(this.directive.payload) : [];
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly directiveService: DirectiveService,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const qrToken = this.route.snapshot.paramMap.get('qrToken');
    if (!qrToken) {
      this.loading = false;
      this.notFound = true;
      return;
    }
    this.directiveService.verify(qrToken).subscribe(
      (response) => {
        if (isMinimalVerifyResponse(response)) {
          this.minimal = response;
        } else {
          this.directive = response;
        }
        this.loading = false;
        this.changeDetector.markForCheck();
      },
      () => {
        this.loading = false;
        this.notFound = true;
        this.changeDetector.markForCheck();
      },
    );
  }
}
